import apiSvc from '../../../core/services/apiSvc';
import validSvc from '../../../core/validSvc';
import utilSvc from '../../../core/utilSvc';
import { stringToBoolean } from '../../../core/table/utils/stringToBoolean';

import { AdminVendorModel, PageMode } from '../vendor_types';
import { FieldError, HttpError422, HttpErrorUpsert, HttpUpsert200 } from '../../../../global';

// read
export const GET_LIST = 'admin/vendor/list'; // we need this back in the repo for same reasons below
const GET_DETAIL = 'admin/vendor/get';

// write
const UPDATE_VENDOR = 'admin/vendor/update';
const CREATE_VENDOR = 'admin/vendor/create';

// AI AGENT: extract base types from AdminVendorModel
// see: base type ValidPropText are string, ValidPropRadioText are boolean, ValidPropDroplistText are string, ValidMultiFieldTextProp are string[]
type AdminVendorModelBaseValues = {
    ObjectState: string;
    Name: string;
    Id: string;
    Type: string;
    SupportsEDI: boolean;
    ProxyVendor: string;
    SenderId: string;
    ReceiverId: string;
    ShortPONames: boolean;
    CSOSSplit: boolean;
    CSOSReceiverId: string;
    CSOSSenderId: string;
    CreditReplenish: boolean;
    CreditReplenishId: string;
    KeyStr: string;
};

// AI AGENT: this is our API JSON contract
// specifically type where UI model differs from API model
type AdminVendorModelAPI = {
    Entity: AdminVendorModelBaseValues & {
        Id: string;
        CashCard: {
            BIN?: string[];
            IncrementalExtractInterval?: number;
            MaxBenefitLevel?: number;
        };
        ExcludedVINs?: string[];
    };
};

export async function getDetail(id: string, type: 'TPAID' | 'VENID' = 'VENID'): Promise<object> {
    // params
    if (!validSvc.isText(id)) {
        throw new Error('vendorRepo.getDetail, id is required');
    }

    // if we return this directly where is the conversion from API to UI edit model?
    // we should be able to return type AdminVendorModel
    // this will be a very big issue with more complex features

    return apiSvc.getEntity(GET_DETAIL, {
        Entity: {
            Params: {
                Id: id,
            },
        },
        Context: {
            [`${type}`]: [id],
        },
    });
}

export async function upsert(m: AdminVendorModel, mode: PageMode): Promise<HttpUpsert200 | HttpErrorUpsert> {
    const url = mode === 'CREATE' ? CREATE_VENDOR : UPDATE_VENDOR;

    // this is implemented as a pass-through, not a data layer, we need the typescript types and logic in this repo with the original API
    // try to re-use everything in the data layer as its our API contract

    // this one type conversion will cover 95% of UI to API property issues
    // its the major value of typescript right here

    // AI AGENT: create the api request object from the edit model
    const apiRequest: AdminVendorModelAPI = {
        Entity: {
            Id: m.Id.val,
            Name: m.Name.val,
            Type: m.Type.val,
            ObjectState: m.ObjectState.val,
            SupportsEDI: stringToBoolean(m.SupportsEDI.val),
            SenderId: m.SenderId.val,
            ReceiverId: m.ReceiverId.val,
            ShortPONames: stringToBoolean(m.ShortPONames.val),
            CSOSSplit: stringToBoolean(m.CSOSSplit.val),
            ProxyVendor: m.ProxyVendor.val,
            CSOSReceiverId: m.CSOSReceiverId.val,
            CSOSSenderId: m.CSOSSenderId.val,
            CashCard: {},
            ExcludedVINs: [],
            CreditReplenish: stringToBoolean(m.CreditReplenish.val),
            CreditReplenishId: m.CreditReplenishId.val,
            // security
            KeyStr: m.KeyStr.val,
        },
    };

    // AI AGENT: additive properties based on type
    switch (m.Type.val) {
        case 'PBM':
            apiRequest.Entity.CashCard = {
                BIN: m.PBMBINItems.val,
                IncrementalExtractInterval: parseInt(m.PBMExtractInterval.val),
                MaxBenefitLevel: parseInt(m.PBMBenefitLevel.val),
            };
            break;
        case 'WHOLESALER':
            apiRequest.Entity.ExcludedVINs = m.WholesalerExcludedVINs.val;
            break;
        case 'MANUFACTURER':
        case 'TPA':
            // no extra properties
            break;
        default:
            throw new Error(`vendorRepo.upsert, unknown vendor type: ${m.Type.val}`);
    }

    // need an await here since API is returning non-edit model property names, this is typical
    // UI hook should have zero concept of the API formats, JSON
    try {
        const result = await apiSvc.postModel(url, apiRequest, true); // set to true to expect a 422
        return result as HttpUpsert200;
    } catch (err: any) {
        return handle422UniqueProperties(err);
    }
}

function handle422UniqueProperties(err: any): HttpErrorUpsert {
    /*
        AI AGENT: unique properties from API back to edit model

        apiRequest.Entity.CashCard = {
            BIN: m.PBMBINItems.val,
            IncrementalExtractInterval: parseInt(m.PBMExtractInterval.val),
            MaxBenefitLevel: parseInt(m.PBMBenefitLevel.val),
        };
        apiRequest.Entity.ExcludedVINs = m.WholesalerExcludedVINs.val;
    */

    // AI AGENT: map all API field error names back to model property
    const vendorFieldApiToModel = new Map<string, FieldError>([
        ['EncryptionPublicKey', { Name: 'KeyStr', Rule: 'Invalid Public Key' }],
        ['BIN', { Name: 'PBMBINItems', Rule: '' }],
    ]);

    err = err as HttpErrorUpsert;
    if (utilSvc.http.isIgnore(err)) {
        return err;
    }

    // 422: switch names where applicable
    if (err.status === 422) {
        err = err as HttpError422;
        const fields = err.data?.Entity?.Fields;
        let nameChange: FieldError | undefined;

        if (Array.isArray(fields)) {
            fields.forEach(field => {
                nameChange = vendorFieldApiToModel.get(field.Name);
                if (nameChange) {
                    field.Name = nameChange.Name;
                    if (nameChange.Rule) {
                        field.Rule = nameChange.Rule; // hard-coded rule
                    }
                }
            });
            err.data.Entity.Fields = fields;
        }
    }

    return err;
}
